import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Table from "react-bootstrap/Table";
import AxisSpinner from "./AxisSpinner";
import NurseActiveNotYetWorkedModal from "../modals/NurseActiveNotYetWorkedModal";
import { BsDownload, BsPersonExclamation, BsSearch } from "react-icons/bs";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import XLSX from "xlsx";

const NurseActiveNotYetWorkedReportTable = () => {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNurse, setSelectedNurse] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const searchRef = useRef();
  const tableRef = useRef();

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    filterReports();
  }, [reports, searchTerm]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await authAxios().get(
        "/api/backoffice/reporting/nurse_active_not_yet_worked",
      );
      setReports(response.data || []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching nurse active not yet worked reports:", err);
      setReports([]);
      setLoading(false);
    }
  };

  const filterReports = () => {
    if (!searchTerm.trim()) {
      setFilteredReports(reports);
      return;
    }

    const filtered = reports.filter((report) => {
      const fullName =
        `${report.user.forename} ${report.user.surname}`.toLowerCase();
      const email = report.user.email.toLowerCase();
      const search = searchTerm.toLowerCase();

      return fullName.includes(search) || email.includes(search);
    });

    setFilteredReports(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleRowClick = (report) => {
    setSelectedNurse(report);
    setShowModal(true);
  };

  const handleUserClick = (row) => {
    navigate(`/users/${row?.user?.id}`);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedNurse(null);
  };

  const exportTable = async () => {
    const wb = XLSX.utils.table_to_book(tableRef.current);
    XLSX.writeFile(wb, "NurseActiveNotYetWorkedReport.xlsx");
  };

  const getErrorBadge = (error) => {
    if (error.startsWith("CertsError:")) {
      return (
        <Badge bg="danger" className="me-1 mb-1">
          {error}
        </Badge>
      );
    } else if (error.startsWith("NMCError:")) {
      return (
        <Badge bg="warning" className="me-1 mb-1">
          {error}
        </Badge>
      );
    } else if (error.startsWith("AxisStartError:")) {
      return (
        <Badge bg="info" className="me-1 mb-1">
          {error}
        </Badge>
      );
    }
    return (
      <Badge bg="secondary" className="me-1 mb-1">
        {error}
      </Badge>
    );
  };

  const getUserStatusBadge = (status) => {
    const statusColors = {
      ACTIVE: "success",
      ONHOLD: "warning",
      BLOCKED: "danger",
    };
    return <Badge bg={statusColors[status] || "secondary"}>{status}</Badge>;
  };

  const sortedReports = filteredReports.sort((a, b) => {
    // Sort by number of errors (descending), then by name
    const errorCountDiff = b.errors.length - a.errors.length;
    if (errorCountDiff !== 0) return errorCountDiff;

    const nameA = `${a.user.forename} ${a.user.surname}`.toLowerCase();
    const nameB = `${b.user.forename} ${b.user.surname}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });

  return (
    <Container fluid>
      <Card className="mb-4">
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">
                <BsPersonExclamation className="me-2" />
                Active Nurses Not Yet Worked Report
              </h5>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => exportTable()}
              >
                <BsDownload className="me-2" />
                Export
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <BsSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by nurse name or email..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </InputGroup>
            </Col>
            <Col md={6} className="text-end">
              <small className="text-muted">
                Showing {filteredReports.length} of {reports.length} nurse
                {reports.length !== 1 ? "s" : ""} with issues
              </small>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <AxisSpinner loading={loading} />
            </div>
          ) : reports.length === 0 ? (
            <Alert variant="success">
              <BsPersonExclamation className="me-2" />
              No active nurses with issues found. All active nurses are ready to
              work!
            </Alert>
          ) : filteredReports.length === 0 ? (
            <Alert variant="warning">
              <BsSearch className="me-2" />
              No nurses match your search criteria.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table hover striped className="mb-0" ref={tableRef}>
                <thead>
                  <tr>
                    <th>Nurse Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Issues Preventing Work</th>
                    <th>Issue Count</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedReports.map((report, index) => (
                    <tr
                      key={index}
                      onClick={() => handleRowClick(report)}
                      style={{ cursor: "pointer" }}
                      title="Click to view nurse issues"
                    >
                      <td onClick={() => handleUserClick(report)}>
                        <strong>
                          {report.user.forename} {report.user.surname}
                        </strong>
                      </td>
                      <td>{report.user.email}</td>
                      <td>{getUserStatusBadge(report.user.status)}</td>
                      <td>
                        <div className="d-flex flex-wrap">
                          {report.errors.map((error, errorIndex) => (
                            <div key={errorIndex}>{getErrorBadge(error)}</div>
                          ))}
                        </div>
                      </td>
                      <td>
                        <Badge
                          bg={report.errors.length > 2 ? "danger" : "warning"}
                        >
                          {report.errors.length}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <NurseActiveNotYetWorkedModal
        visible={showModal}
        close={handleCloseModal}
        nurseReport={selectedNurse}
      />
    </Container>
  );
};

export default NurseActiveNotYetWorkedReportTable;
